import { useEffect, useState } from "react";
import { money } from "@/lib/api";

const fmt = (iso) => (iso ? String(iso).slice(0, 16).replace("T", " ") : "—");

function Section({ title, count, children, testid }) {
  return (
    <div style={{ marginTop: 18 }} data-testid={testid}>
      <h4 style={{ marginBottom: 8 }}>{title} <span className="muted small">({count})</span></h4>
      {count === 0 ? <div className="muted small">None yet.</div> : children}
    </div>
  );
}

export function TesterProfile({ c, testerId, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => c.testerDetail(testerId).then(setD).catch(() => setErr(true));
  useEffect(() => { setD(null); setErr(false); load(); }, [c, testerId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function addNote() {
    if (note.trim().length < 2) return;
    setBusy(true);
    try { await c.testerNote(testerId, note.trim()); setNote(""); await load(); } finally { setBusy(false); }
  }
  async function toggleFlag(f) {
    const cur = d.tester.flags || [];
    const next = cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f];
    setBusy(true);
    try { await c.testerFlags(testerId, next); await load(); } finally { setBusy(false); }
  }

  const t = d?.tester;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(20,30,70,.45)", zIndex: 50, display: "flex", justifyContent: "flex-end" }} onClick={onClose}>
      <aside className="panel" data-testid="tester-profile" onClick={(e) => e.stopPropagation()}
        style={{ background: "var(--paper)", width: "100%", maxWidth: 760, height: "100%", overflow: "auto", padding: 26, boxShadow: "var(--shadow)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            {t ? <>
              <span className={`pill ${["bounced", "unsubscribed"].includes(t.state) ? "fail" : "neutral"}`}>{t.state}</span>
              <h2 style={{ margin: "10px 0 4px" }} data-testid="tester-profile-name">{t.name || t.email}</h2>
              <div className="muted small">{t.email}{t.phone ? ` · ${t.phone}` : ""}{t.city ? ` · ${t.city}` : ""}</div>
              <div className="small" style={{ marginTop: 8, display: "flex", gap: 14, flexWrap: "wrap" }}>
                <span>UPI <b>{t.upi || "—"}</b></span><span>Band <b>{t.band || "—"}</b></span>
                <span>TesterRank <b>{t.testerrank ?? "—"}</b></span><span>TrustRank <b>{t.trustrank ?? "—"}</b></span>
                <span>Invites <b>{t.inviteCount ?? 0}</b></span><span>Submitted <b>{t.submittedCount ?? 0}</b></span><span>Source <b>{t.source || "—"}</b></span>
              </div>
            </> : <h2>{err ? "Tester not found" : "Loading…"}</h2>}
          </div>
          <button className="btn ghost sm" data-testid="tester-profile-close" onClick={onClose}>Close</button>
        </div>

        {d && <>
          <div style={{ marginTop: 18 }} data-testid="profile-flags">
            <h4 style={{ marginBottom: 8 }}>Flags <span className="muted small">(affect dispatch)</span></h4>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {(d.available_flags || []).map((f) => { const on = (t.flags || []).includes(f); return (
                <button key={f} type="button" className={`btn sm ${on ? (f === "do_not_dispatch" ? "" : "green") : "ghost"}`} data-testid={`flag-${f}`} disabled={busy} onClick={() => toggleFlag(f)}
                  style={on && f === "do_not_dispatch" ? { background: "var(--fail)", borderColor: "var(--fail)" } : undefined}>{on ? "✓ " : ""}{f.replace(/_/g, " ")}</button>); })}
            </div>
            <p className="hint" style={{ marginTop: 6 }}>“do not dispatch” removes this tester from all future session offers.</p>
          </div>

          <div style={{ marginTop: 18 }} data-testid="profile-notes">
            <h4 style={{ marginBottom: 8 }}>Private notes <span className="muted small">({t.admin_notes?.length || 0})</span></h4>
            <div style={{ display: "flex", gap: 8 }}>
              <input data-testid="note-input" placeholder="e.g. Great on Realme devices, slow to submit media" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} style={{ flex: 1 }} />
              <button className="btn sm" data-testid="note-add" disabled={busy || note.trim().length < 2} onClick={addNote}>Add note</button>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: "10px 0 0", fontSize: 14 }}>
              {(t.admin_notes || []).map((n) => <li key={n.id} data-testid={`note-${n.id}`} style={{ padding: "8px 0", borderBottom: "1px solid var(--rule)" }}>{n.text}<div className="muted small">{n.by} · {fmt(n.at)}</div></li>)}
            </ul>
          </div>

          <Section title="Devices" count={t.devices?.length || 0} testid="profile-devices">
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {(t.devices || []).map((dv, i) => <span key={i} className="tag">{[dv.brand, dv.model, dv.browser, dv.os || dv.platform, dv.os_version].filter(Boolean).join(" · ")}</span>)}
            </div>
          </Section>

          <Section title="Sessions" count={d.sessions.length} testid="profile-sessions">
            <table className="tbl"><thead><tr><th>Job</th><th>State</th><th>Band</th><th>Device</th><th>Cases</th><th>Started</th></tr></thead>
              <tbody>{d.sessions.map((s) => {
                const r = Object.values(s.results || {});
                return <tr key={s.id}><td>{s.job_code}</td><td><span className="pill neutral">{s.state}</span></td><td>{s.band}</td>
                  <td className="small">{[s.device?.brand, s.device?.model, s.device?.browser].filter(Boolean).join(" ") || s.device?.platform}</td>
                  <td className="small">{s.cases} · {r.filter((x) => x === "pass").length}✓ {r.filter((x) => x === "fail").length}✕</td><td className="small">{fmt(s.startedAt)}</td></tr>;
              })}</tbody></table>
          </Section>

          <Section title="Findings" count={d.findings.length} testid="profile-findings">
            <table className="tbl"><thead><tr><th>Sev</th><th>Title</th><th>Status</th><th>Media</th><th className="c">Bonus</th></tr></thead>
              <tbody>{d.findings.map((f) => <tr key={f.id}><td><span className="tag">{f.severity}</span></td><td>{f.title}</td>
                <td><span className={`pill ${f.verify_status === "accepted" ? "" : f.verify_status === "rejected" ? "fail" : "neutral"}`}>{f.verify_status}</span></td>
                <td>{f.media}</td><td className="c num">{f.bonus ? money(f.bonus, "INR") : "—"}</td></tr>)}</tbody></table>
          </Section>

          <Section title="Payouts" count={d.payouts.length} testid="profile-payouts">
            <table className="tbl"><thead><tr><th>Job</th><th className="c">Amount</th><th>State</th><th>Paid</th><th>Reference</th></tr></thead>
              <tbody>{d.payouts.map((p) => <tr key={p.id}><td>{p.job_code}</td><td className="c num">{money(p.amount, "INR")}</td>
                <td><span className={`pill ${["held", "failed"].includes(p.state) ? "fail" : p.state === "paid" ? "" : "neutral"}`}>{p.state}</span></td>
                <td className="small">{fmt(p.paidAt)}</td><td className="small">{p.reference || "—"}</td></tr>)}</tbody></table>
          </Section>

          <Section title="Emails" count={d.emails.length} testid="profile-emails">
            <table className="tbl"><thead><tr><th>Subject</th><th>Status</th><th>When</th></tr></thead>
              <tbody>{d.emails.map((e, i) => <tr key={i}><td>{e.subject}</td>
                <td><span className={`pill ${e.ok ? "" : "fail"}`}>{e.ok ? (e.last_event ? e.last_event.replace("email.", "") : "sent") : "failed"}</span>{e.error && <div className="muted small">{String(e.error).slice(0, 80)}</div>}</td>
                <td className="small">{fmt(e.at)}</td></tr>)}</tbody></table>
          </Section>

          <Section title="Timeline" count={d.events.length} testid="profile-events">
            <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 14 }}>
              {d.events.map((e, i) => <li key={i} style={{ padding: "6px 0", borderBottom: "1px solid var(--rule)", display: "flex", justifyContent: "space-between", gap: 12 }}>
                <span><b>{e.type}</b> <span className="muted small">{e.actor}</span></span><span className="muted small">{fmt(e.at)}</span></li>)}
            </ul>
          </Section>
        </>}
      </aside>
    </div>
  );
}
